import { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Workflow, Eye } from 'lucide-react';
import { SearchDemo } from './SearchDemo';
import { PipelineDemo } from './PipelineDemo';
import { useT } from '@/i18n/provider';
import { cn } from '@/lib/utils';

const AUTO_SWITCH_INTERVAL = 24_000; // ms — must be longer than SearchDemo total (~22s)

const TABS = [
  { id: 'search' as const, icon: Search, labelKey: 'productTour.tab.search' },
  { id: 'pipeline' as const, icon: Workflow, labelKey: 'productTour.tab.pipeline' },
] as const;

type TabId = (typeof TABS)[number]['id'];

export function ProductTourSection() {
  const t = useT();
  const [activeTab, setActiveTab] = useState<TabId>('search');
  const [userPinned, setUserPinned] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);

  // Intersection observer for visibility
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => setIsInView(entry.isIntersecting),
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Auto-switch tabs
  useEffect(() => {
    if (userPinned || !isInView) return;

    const timer = setInterval(() => {
      setActiveTab((prev) => (prev === 'search' ? 'pipeline' : 'search'));
      // Restart progress bar animation
      if (progressRef.current) {
        progressRef.current.style.animation = 'none';
        // Force reflow
        void progressRef.current.offsetHeight;
        progressRef.current.style.animation = '';
      }
    }, AUTO_SWITCH_INTERVAL);

    return () => clearInterval(timer);
  }, [userPinned, isInView]);

  const handleTabClick = useCallback((tabId: TabId) => {
    setActiveTab(tabId);
    setUserPinned(true);
    // Reset progress bar
    if (progressRef.current) {
      progressRef.current.style.animation = 'none';
    }
  }, []);

  return (
    <section
      ref={sectionRef}
      id="product-tour"
      className="py-24 sm:py-32 bg-gray-50 dark:bg-gray-950"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="max-w-2xl mx-auto text-center mb-12 sm:mb-16 animate-on-scroll">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800/30 mb-4">
            <Eye className="h-3.5 w-3.5 text-primary-500" />
            <span className="text-xs font-medium text-primary-700 dark:text-primary-300">
              {t('productTour.badge')}
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 dark:text-white text-balance">
            {t('productTour.title')}
          </h2>
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-400 text-balance">
            {t('productTour.subtitle')}
          </p>
        </div>

        {/* Feature tabs */}
        <div className="flex justify-center mb-8 animate-on-scroll">
          <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-gray-200/80 dark:bg-gray-800/80 backdrop-blur-sm">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabClick(tab.id)}
                  className={cn(
                    'relative flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-300',
                    isActive
                      ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                  )}
                >
                  <Icon className="h-4 w-4" />
                  <span>{t(tab.labelKey)}</span>
                  {/* Auto-advance progress bar */}
                  {isActive && !userPinned && isInView && (
                    <div className="absolute bottom-0 left-2 right-2 h-0.5 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700">
                      <div
                        ref={activeTab === tab.id ? progressRef : undefined}
                        className="h-full bg-primary-500 rounded-full"
                        style={{
                          animation: `productTourProgress ${AUTO_SWITCH_INTERVAL}ms linear forwards`,
                        }}
                      />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Demo container */}
        <div className="animate-on-scroll relative max-w-6xl mx-auto">
          {/* Glow effect */}
          <div className="absolute -inset-4 -z-10 bg-gradient-to-r from-primary-500/10 via-primary-400/5 to-primary-500/10 rounded-3xl blur-2xl opacity-60" />

          {/* Cross-fade between demos — fixed height prevents layout shifts */}
          <div className="relative h-[480px] sm:h-[540px]">
            <div
              className={cn(
                'absolute inset-0 transition-all duration-500',
                activeTab === 'search'
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-2 pointer-events-none'
              )}
            >
              <SearchDemo enabled={isInView && activeTab === 'search'} />
            </div>
            <div
              className={cn(
                'absolute inset-0 transition-all duration-500',
                activeTab === 'pipeline'
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-2 pointer-events-none'
              )}
            >
              <PipelineDemo enabled={isInView && activeTab === 'pipeline'} />
            </div>
          </div>
        </div>

        {/* Feature description */}
        <div className="mt-10 max-w-3xl mx-auto animate-on-scroll">
          <p className="text-center text-base text-gray-500 dark:text-gray-400">
            {activeTab === 'search'
              ? t('productTour.search.caption')
              : t('productTour.pipeline.caption')}
          </p>
        </div>
      </div>
    </section>
  );
}
